import type { AudienceInput, AudienceView, ListingState, LiveAudience, OwnerView } from '@ralph/contracts'
import { publicationGoesLiveNow, splitChange } from '@ralph/contracts'
import type { I18n, Messages } from '@/i18n/index.ts'

/**
 * What confirming the publish dialog will do, and what it is called (PRD-20 OWN-012/013,
 * PRD-26 D-13, D-40, D-50).
 *
 * Three registers, decided here and nowhere else. Turning an agent on for the people who maintain
 * it is not a handover, and a checkbox reading "I am sharing it" would be the product asserting
 * something untrue on the screen whose job is to be exact. Sharing with people the maker may share
 * with alone IS a handover. And a choice that reaches a group they do not manage, or everyone, is
 * split: what can go live does, the rest becomes a sharing request, and the button says **Send for
 * review** — because that is what pressing it does.
 *
 * The prediction uses the contracts' `splitChange`, the function the server decides with, plus the
 * one rule the server adds around it (D-50): a published agent always takes its new version at
 * once, while one that is not live stays as it is when nothing of the choice can go live yet. The
 * server's answer is the one that counts — a manager removed a moment ago is judged as they stand
 * then — and the dialog shows what came back.
 *
 * Pure, so the rule can be tested without a DOM: the interesting cases are the crossings of state,
 * action and split, and three call sites each holding a pair of ternaries is how two of them end up
 * disagreeing.
 */

/**
 * The catalogue, handed in rather than captured.
 *
 * `const strings = makerStrings.publish` at module scope is what stood here, and it is the
 * exact shape PRD-27 F-3 names: the branch is read once, when the bundle evaluates, and every
 * sentence below then renders whichever language was live at that moment for the rest of the
 * session — with no type error, no runtime error and no failing test. Every caller already
 * holds `useI18n()`, so the fix is a parameter.
 */
type PublishWords = Messages['marketplace']['publish']

export type PublishViewer = { id: string | null; isAdmin: boolean; managedGroupIds: readonly string[] }

export type PublishPlan = {
  /** `review` whenever anything waits for an administrator; otherwise private or share. */
  kind: 'private' | 'share' | 'review'
  /** Whether anything goes live the moment the maker confirms. */
  goesLiveNow: boolean
  /** Whether confirming changes who can use it (as opposed to only publishing a new version). */
  changesNow: boolean
  /** Who can use it once confirmed; `null` when nothing goes live yet. */
  now: LiveAudience | null
  /** What waits for an administrator; `null` when nothing does. */
  review: { kind: 'groups' | 'everyone'; groupIds: string[] } | null
}

const toLive = (audience: AudienceInput): LiveAudience => ({
  kind: audience.kind,
  groupIds: audience.kind === 'groups' ? [...new Set(audience.groupIds)] : []
})

export const planPublication = (input: {
  action: 'publish' | 'restore'
  state: ListingState
  live: LiveAudience
  requested: AudienceInput
  viewer: PublishViewer
}): PublishPlan => {
  const requested = toLive(input.requested)
  const split = splitChange({
    actorIsAdmin: input.viewer.isAdmin,
    actorManagedGroupIds: input.viewer.managedGroupIds,
    live: input.live,
    requested
  })
  // The server's own rule, so the promise the maker acknowledges is what then happens.
  const goesLiveNow = publicationGoesLiveNow({ action: input.action, state: input.state, requested, split })

  return {
    kind: split.review !== null ? 'review' : requested.kind === 'private' ? 'private' : 'share',
    goesLiveNow,
    changesNow: split.now !== null,
    now: goesLiveNow ? (split.now ?? input.live) : null,
    review: split.review
  }
}

// ---------------------------------------------------------------------------
// The words
// ---------------------------------------------------------------------------

export type PublishWording = {
  /** The lifecycle bar's primary button. */
  action: string
  title: string
  lead: string
  consentHeading: string
  ackLabel: string
  /** The dialog's own confirm button. */
  confirm: string
}

export const publishWording = (
  t: PublishWords,
  plan: PublishPlan,
  input: { action: 'publish' | 'restore'; hasPublishedBefore: boolean }
): PublishWording => {
  if (plan.kind === 'review') {
    return {
      action: t.sendForReview,
      title: t.titleReview,
      lead: t.leadReview,
      consentHeading: t.consentHeading,
      ackLabel: t.ackLabelReview,
      confirm: t.confirmReview
    }
  }

  const priv = plan.kind === 'private'
  if (input.action === 'restore') {
    return {
      action: t.restore,
      title: t.titleRestore,
      lead: priv ? t.leadPrivate : t.leadRestore,
      consentHeading: priv ? t.consentHeadingPrivate : t.consentHeading,
      ackLabel: priv ? t.ackLabelPrivate : t.ackLabel,
      confirm: t.confirmRestore
    }
  }

  if (priv) {
    return {
      action: input.hasPublishedBefore ? t.republishPrivate : t.openPrivate,
      title: t.titlePrivate,
      lead: t.leadPrivate,
      consentHeading: t.consentHeadingPrivate,
      ackLabel: t.ackLabelPrivate,
      confirm: t.confirmPrivate
    }
  }

  return {
    action: input.hasPublishedBefore ? t.republish : t.open,
    title: t.title,
    lead: t.lead,
    consentHeading: t.consentHeading,
    ackLabel: t.ackLabel,
    confirm: t.confirm
  }
}

/** Turns an audience into words: the caller knows the names, sizes and the private label. */
export type DescribeAudience = (audience: {
  kind: 'private' | 'groups' | 'everyone'
  groupIds: readonly string[]
}) => string

/**
 * What happens now, and what happens if an administrator approves — the two lines the dialog puts
 * above "What they get". A published agent's new version goes live at once whatever waits (D-50),
 * and saying so is the point: the maker is about to change what people already use.
 */
export const publishStatements = (
  t: PublishWords,
  plan: PublishPlan,
  input: { state: ListingState; action: 'publish' | 'restore'; describe: DescribeAudience }
): { now: string; ifApproved: string | null } => {
  const now = (() => {
    if (!plan.goesLiveNow || plan.now === null) {
      return input.state === 'archived' ? t.now.waitsArchived : t.now.waitsDraft
    }
    if (input.state === 'published' && input.action === 'publish') {
      return plan.changesNow ? t.now.liveChanged(input.describe(plan.now)) : t.now.liveVersion
    }
    return t.now.live(input.describe(plan.now))
  })()

  const ifApproved =
    plan.review === null
      ? null
      : plan.review.kind === 'everyone'
        ? t.ifApprovedEveryone
        : t.ifApproved(input.describe(plan.review))

  return { now, ifApproved }
}

/**
 * What the dialog's outcome says, from what the SERVER did — `published` and `request` — rather
 * than from the prediction, which a moment's change of standing can make wrong.
 */
export const publishOutcomeWording = (
  t: PublishWords,
  fmt: Pick<I18n, 'formatList'>,
  outcome: {
    published: boolean
    request: { requestedKind: 'groups' | 'everyone'; groups: Array<{ name: string }> } | null
  },
  input: { plan: PublishPlan; action: 'publish' | 'restore'; who: string; personal: boolean }
): { title: string; body: string; note: string | null } => {
  if (outcome.request !== null) {
    const whom =
      outcome.request.requestedKind === 'everyone'
        ? t.lifecycle.request.everyone
        : fmt.formatList(outcome.request.groups.map((group) => group.name))
    return {
      title: outcome.published ? t.sentAndPublishedTitle : t.sentTitle,
      body: t.sentBody(whom),
      note: outcome.published ? t.sentLiveNote : t.sentWaitNote
    }
  }
  if (input.plan.kind === 'private') {
    return {
      title: input.action === 'restore' ? t.publishedTitleRestore : t.publishedTitlePrivate,
      body: t.publishedBodyPrivate(input.personal),
      note: null
    }
  }
  return {
    title: input.action === 'restore' ? t.publishedTitleRestore : t.publishedTitle,
    body: t.publishedBody(input.who),
    note: null
  }
}

// ---------------------------------------------------------------------------
// Who "only its maintainers" is
// ---------------------------------------------------------------------------

/**
 * The picker's private label (D-37): "Only me", "Only Ana Souza", "Only Finance EMEA’s managers".
 *
 * Two of the three words belong to the GROUPS namespace — the picker owns them, and every
 * surface that shows a private audience shows the picker's wording — and the third is the
 * maker's. So this one function takes the whole catalogue rather than the publish slice its
 * neighbours take: it is genuinely a sentence about two surfaces, and the alternative is a
 * module-scope read of somebody else's catalogue, which freezes the language (PRD-27 F-3).
 */
export const privateLabelFor = (t: Messages, owner: OwnerView, viewerId: string | null): string =>
  owner.kind === 'group'
    ? t.groups.privateLabel.groupManagers(owner.group.name)
    : owner.id === viewerId
      ? t.groups.privateLabel.me
      : t.marketplace.publish.privateOwner(owner.name)

/** The same people as the subject of a sentence the maker reads: "Only you will get…". */
export const privateSubjectFor = (t: PublishWords, owner: OwnerView, viewerId: string | null): string =>
  owner.kind === 'group'
    ? t.privateManagers(owner.group.name)
    : owner.id === viewerId
      ? t.privateYou
      : t.privateOwner(owner.name)

/**
 * The same people again, read mid-line: "…published, only you".
 *
 * A second entry rather than lower-casing the subject at the call site (PRD-27 D-20). The shelf
 * used to do exactly that, and it was wrong on its way to being wrong in Portuguese: "Only Ana
 * Souza" lower-cased is "only Ana Souza" by luck, because the first character happened to be
 * the one word that may change case. A German or a Portuguese sentence has no such luck, and
 * neither does an English one whose subject starts with a proper noun.
 */
export const privateSubjectPhraseFor = (t: PublishWords, owner: OwnerView, viewerId: string | null): string =>
  owner.kind === 'group'
    ? t.privateManagersPhrase(owner.group.name)
    : owner.id === viewerId
      ? t.privateYouPhrase
      : t.privateOwnerPhrase(owner.name)

/**
 * What a published agent's bar says about who can use it.
 *
 * "The people it is shared with can find it" is true and useless when those people are its
 * maintainers.
 */
export const publishedStateHint = (
  t: PublishWords,
  audience: AudienceView,
  owner: OwnerView,
  viewerId: string | null
): string =>
  audience.kind === 'private'
    ? t.lifecycle.publishedHintPrivate(owner.kind === 'user' && owner.id === viewerId)
    : t.lifecycle.stateHint.published
